import React, { Suspense } from 'react';

import * as Sentry from '@sentry/browser';
import { SnackbarProvider } from 'notistack';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryProvider } from '~/lib/tanstack-query/tanstack-query.tsx';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LicenseInfo } from '@mui/x-license';

import ConfirmDialogProvider from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';

import { AuthRouter, HomeRoute, init as initializeAuth } from './auth';
import App from './components/App';
import store from './configureStore';
import './scss/main.scss';
import { theme } from './theme';
import './timeInit';

/***** configurations */
initializeAuth({
  endpoints: {
    baseUri: window.configs.AUTH_HOST,
  },
});

const releaseVersion = window.configs.RELEASE_VERSION;
const appEnvironment = window.configs.APP_ENV;

if (appEnvironment.startsWith('test') || appEnvironment.startsWith('production')) {
  Sentry.init({
    dsn: 'https://122f65dfe1d549b5a5b5c4ef9ac0073d@sentry.io/1760579',
    environment: appEnvironment,
    release: releaseVersion || 'unreleased',
    ignoreErrors: ['CustomerSubscriptions not found', 'PermissionError'],
  });
}

const appContainerEl = document.getElementById('app-container');
const root = createRoot(appContainerEl);

LicenseInfo.setLicenseKey(window.configs.MUI_X_LICENSE_KEY);

// Render app
root.render(
  <ThemeProvider theme={theme}>
    <QueryProvider>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
        <Provider store={store}>
          <SnackbarProvider anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} maxSnack={10}>
            <ConfirmDialogProvider>
              <AuthRouter>
                <HomeRoute path="/">
                  <Suspense fallback={<Spinner />}>
                    <App />
                  </Suspense>
                </HomeRoute>
              </AuthRouter>
            </ConfirmDialogProvider>
          </SnackbarProvider>
        </Provider>
      </LocalizationProvider>
    </QueryProvider>
  </ThemeProvider>,
);
